import React, { useState, useEffect } from 'react';
import './Logbook.css';
import './App.css';  // for company-popup modal styles
import { ENTRY_LABEL, EXIT_LABEL } from './labels';

const Logbook = () => {
  const [logData, setLogData] = useState(null);
  const [quotes, setQuotes] = useState({}); // symbol -> { price, stop_loss, ... }
  const [quotesRefreshing, setQuotesRefreshing] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [sortField, setSortField] = useState('last_found');
  const [sortDirection, setSortDirection] = useState('desc');
  const [sundayOnly, setSundayOnly] = useState(false);
  const [companyPopup, setCompanyPopup] = useState({ show: false, data: null, loading: false });

  const API_URL = process.env.REACT_APP_BACKEND_URL || '';

  useEffect(() => {
    fetchLogbook();
    fetchQuotes();
  }, []);

  const fetchLogbook = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${API_URL}/api/logbook`);
      if (!response.ok) throw new Error('Failed to fetch logbook');
      const data = await response.json();
      setLogData(data);
      setError(null);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const fetchQuotes = async (refresh = false) => {
    try {
      const res = await fetch(`${API_URL}/api/logbook/quotes${refresh ? '?refresh=true' : ''}`);
      const data = await res.json();
      if (data.status === 'success') {
        setQuotes(data.quotes || {});
        setQuotesRefreshing(!!data.refresh_in_progress);
        // If a refresh is running, poll every 4s until complete
        if (data.refresh_in_progress) {
          setTimeout(() => fetchQuotes(false), 4000);
        }
      }
    } catch (err) {
      // Non-fatal; the price/stop columns will just show "—"
    }
  };

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('desc');
    }
  };

  const getSortedEntries = () => {
    if (!logData?.entries) return [];
    
    return [...logData.entries].sort((a, b) => {
      let aVal = a[sortField];
      let bVal = b[sortField];
      
      if (sortField === 'times_found') {
        aVal = Number(aVal) || 0;
        bVal = Number(bVal) || 0;
      }
      
      if (aVal < bVal) return sortDirection === 'asc' ? -1 : 1;
      if (aVal > bVal) return sortDirection === 'asc' ? 1 : -1;
      // tie-break: most recently discovered first
      if (a.first_discovered < b.first_discovered) return 1;
      if (a.first_discovered > b.first_discovered) return -1;
      return 0;
    });
  };

  const downloadCSV = () => {
    window.open(`${API_URL}/api/logbook/csv`, '_blank');
  };

  const getSortIcon = (field) => {
    if (sortField !== field) return '↕️';
    return sortDirection === 'asc' ? '↑' : '↓';
  };

  // Company description modal handlers (mirrors App.js)
  const handleCompanyClick = async (entry, e) => {
    e.preventDefault();
    setCompanyPopup({ show: true, data: { symbol: entry.symbol, company_name: entry.company_name }, loading: true });
    try {
      const res = await fetch(`${API_URL}/api/company/${entry.symbol}?_=${Date.now()}`, { cache: 'no-store' });
      if (!res.ok) {
        setCompanyPopup({
          show: true,
          data: {
            symbol: entry.symbol,
            company_name: entry.company_name,
            business_description: '',
            company_description_full: `Description fetch failed (HTTP ${res.status}). Try refreshing.`,
          },
          loading: false,
        });
        return;
      }
      const data = await res.json();
      if (data.status === 'success' && data.company_description_full) {
        setCompanyPopup({ show: true, data, loading: false });
      } else {
        setCompanyPopup({
          show: true,
          data: {
            symbol: entry.symbol,
            company_name: entry.company_name,
            business_description: data.business_description || '',
            company_description_full: data.company_description_full || data.message || 'Company description not available.',
          },
          loading: false,
        });
      }
    } catch (err) {
      setCompanyPopup({
        show: true,
        data: {
          symbol: entry.symbol,
          company_name: entry.company_name,
          business_description: '',
          company_description_full: `Could not load description: ${err.message || 'network error'}.`,
        },
        loading: false,
      });
    }
  };

  const closeCompanyPopup = () => setCompanyPopup({ show: false, data: null, loading: false });

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (companyPopup.show && !event.target.closest('.company-popup') && !event.target.closest('.company-name-clickable')) {
        closeCompanyPopup();
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [companyPopup.show]);

  if (loading) {
    return (
      <div className="logbook-container">
        <div className="logbook-loading">
          <div className="spinner"></div>
          <p>Loading Stock Discovery Logbook...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="logbook-container">
        <div className="logbook-error">
          <h2>Error Loading Logbook</h2>
          <p>{error}</p>
          <button onClick={fetchLogbook}>Try Again</button>
        </div>
      </div>
    );
  }

  const entries = getSortedEntries().filter(
    (e) => !sundayOnly || ['entered', 'exited'].includes(quotes[e.symbol]?.status),
  );
  const sundayCount = (logData?.entries || []).filter(
    (e) => ['entered', 'exited'].includes(quotes[e.symbol]?.status),
  ).length;

  // Completed Entry -> Exit pairs across the whole logbook. Open positions are excluded
  // on purpose: an unrealised gain is not a result.
  const closedTrades = Object.values(quotes).flatMap((q) => q?.trade_history || []);
  const wins = closedTrades.filter((t) => t.win);
  const losses = closedTrades.filter((t) => !t.win);
  const avg = (arr) => (arr.length ? arr.reduce((s, t) => s + t.pct_return, 0) / arr.length : 0);
  const winRate = closedTrades.length ? (wins.length / closedTrades.length) * 100 : null;

  return (
    <div className="logbook-container">
      <header className="logbook-header">
        <h1>📊 Stock Discovery Logbook</h1>
        <p className="logbook-subtitle">Track first appearances and recurring selections</p>
      </header>

      <div className="logbook-stats">
        <div className="stat-card">
          <span className="stat-value">{logData?.total_unique_stocks || 0}</span>
          <span className="stat-label">Unique Stocks</span>
        </div>
        <div className="stat-card">
          <span className="stat-value">{logData?.total_appearances || 0}</span>
          <span className="stat-label">Total Appearances</span>
        </div>
        <div className="stat-card">
          <span className="stat-value">
            {logData?.last_updated ? new Date(logData.last_updated).toLocaleDateString() : 'N/A'}
          </span>
          <span className="stat-label">Last Updated</span>
        </div>
        <div className="stat-card" data-testid="breakout-record-card"
          title={closedTrades.length
            ? `${wins.length} ${wins.length === 1 ? 'win' : 'wins'} averaging +${avg(wins).toFixed(1)}%  •  ${losses.length} ${losses.length === 1 ? 'loss' : 'losses'} averaging ${avg(losses).toFixed(1)}%  •  open positions excluded`
            : 'No Entry → Exit pair has completed yet. The record starts filling once a position that entered closes below its 50-day line.'}>
          <span className="stat-value">
            {closedTrades.length
              ? <>
                  <span className="rec-win">{wins.length}W</span>
                  <span className="rec-sep"> · </span>
                  <span className="rec-loss">{losses.length}L</span>
                </>
              : '—'}
          </span>
          <span className="stat-label">
            {winRate != null ? `Breakout Record · ${winRate.toFixed(0)}% win rate` : 'Breakout Record · no closed trades yet'}
          </span>
        </div>
      </div>

      <div className="logbook-actions">
        <button className="download-btn" onClick={downloadCSV}>
          📥 Download CSV
        </button>
        <button className="refresh-btn" onClick={() => { fetchLogbook(); fetchQuotes(true); }}>
          🔄 Refresh
        </button>
        <button
          className={`sunday-review-btn ${sundayOnly ? 'active' : ''}`}
          onClick={() => setSundayOnly(!sundayOnly)}
          title="Show only symbols currently carrying an Entry or Exit signal"
          data-testid="signals-review-toggle"
        >
          🎯 Signals ({sundayCount})
        </button>
        {quotesRefreshing && (
          <span className="quotes-refreshing-tag" data-testid="quotes-refreshing-tag">
            Refreshing prices…
          </span>
        )}
      </div>

      {entries.length === 0 ? (
        <div className="logbook-empty">
          <p>No stocks discovered yet. The logbook will populate after the next screening.</p>
        </div>
      ) : (
        <div className="logbook-table-wrapper">
          <table className="logbook-table">
            <thead>
              <tr>
                <th onClick={() => handleSort('symbol')}>
                  Symbol {getSortIcon('symbol')}
                </th>
                <th onClick={() => handleSort('company_name')}>
                  Company {getSortIcon('company_name')}
                </th>
                <th>Current Price</th>
                <th title="Stop loss = max gap between price - (2 × ATR) and price - 7%, whichever is looser">
                  Entry Stop
                </th>
                <th title="Entry = the next open after a confirmed breakout (close above resistance on strong volume). Exit = the next open after a close below the 50-day line. The 50-day value is shown on hover.">
                  {ENTRY_LABEL} / {EXIT_LABEL}
                </th>
                <th title="Win/loss record of completed Entry → Exit pairs for this symbol, measured close-to-close. An open position shows its mark-to-date separately and is NOT counted as a result.">
                  Record
                </th>
                <th onClick={() => handleSort('first_discovered')}>
                  First Discovered {getSortIcon('first_discovered')}
                </th>
                <th onClick={() => handleSort('times_found')}>
                  Times Found {getSortIcon('times_found')}
                </th>
                <th onClick={() => handleSort('last_found')}>
                  Last Found {getSortIcon('last_found')}
                </th>
                <th>Sector</th>
                <th>All Appearances</th>
              </tr>
            </thead>
            <tbody>
              {entries.map((entry, index) => (
                <tr key={entry.symbol || index}>
                  <td className="symbol-cell">
                    <a
                      href={`https://finance.yahoo.com/quote/${entry.symbol}/chart/`}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      {entry.symbol}
                    </a>
                  </td>
                  <td className="company-cell">
                    <div
                      className="company-name company-name-clickable"
                      onClick={(e) => handleCompanyClick(entry, e)}
                      title="Click for company description"
                    >
                      {entry.company_name}
                    </div>
                  </td>
                  <td className="price-cell" data-testid={`logbook-price-${entry.symbol}`}>
                    {quotes[entry.symbol]?.price !== undefined
                      ? `$${quotes[entry.symbol].price.toFixed(2)}`
                      : <span className="quote-pending">—</span>}
                  </td>
                  <td className="stop-cell" data-testid={`logbook-stop-${entry.symbol}`}>
                    {quotes[entry.symbol]?.stop_loss !== undefined
                      ? (
                        <span
                          title={`Rule: ${quotes[entry.symbol].stop_rule}  •  2×ATR stop: $${quotes[entry.symbol].stop_atr?.toFixed(2)}  •  7% stop: $${quotes[entry.symbol].stop_pct?.toFixed(2)}`}
                          className="stop-value"
                        >
                          ${quotes[entry.symbol].stop_loss.toFixed(2)}
                        </span>
                      )
                      : <span className="quote-pending">—</span>}
                  </td>
                  <td className="trail-cell" data-testid={`logbook-signal-${entry.symbol}`}>
                    {(() => {
                      const q = quotes[entry.symbol];
                      if (!q || !q.status || q.status === 'watch') return <span className="quote-pending">—</span>;
                      const ma = q.ma50 != null ? `50-day line: $${q.ma50.toFixed(2)}` : '';
                      if (q.status === 'entered') {
                        const tip = [`${ENTRY_LABEL} signal: breakout above $${q.resistance ?? '—'} on ${q.breakout_rvol ?? '?'}× volume`, `Entered ${q.entry_date} (next open)`, ma, 'Exit fires on a close below the 50-day line'].filter(Boolean).join('  •  ');
                        return (
                          <span className="trail-value sunday-raised" title={tip}>
                            {ENTRY_LABEL} · {q.entry_date}
                          </span>
                        );
                      }
                      if (q.status === 'exited') {
                        const tip = [`${EXIT_LABEL} signal: closed below the 50-day line`, `Exited ${q.exit_date} (next open)`, ma].filter(Boolean).join('  •  ');
                        return (
                          <span className="trail-value sunday-stopped" title={tip}>
                            🔴 {EXIT_LABEL} · {q.exit_date}
                          </span>
                        );
                      }
                      return <span className="quote-pending">—</span>;
                    })()}
                  </td>
                  <td className="record-cell" data-testid={`logbook-record-${entry.symbol}`}>
                    {(() => {
                      const q = quotes[entry.symbol];
                      const hist = q?.trade_history || [];
                      if (!hist.length && q?.open_pct == null) return <span className="quote-pending">—</span>;
                      const w = hist.filter((t) => t.win).length;
                      const l = hist.length - w;
                      const tip = [
                        ...hist.map((t) => `${t.entry_date} → ${t.exit_date}: ${t.pct_return > 0 ? '+' : ''}${t.pct_return}%`),
                        q?.open_pct != null ? `open since ${q.entry_date}: ${q.open_pct > 0 ? '+' : ''}${q.open_pct}% (not counted)` : '',
                      ].filter(Boolean).join('  •  ');
                      return (
                        <span className="record-value" title={tip}>
                          {hist.length > 0 && (
                            <>
                              <span className="rec-win">{w}W</span>
                              <span className="rec-sep">·</span>
                              <span className="rec-loss">{l}L</span>
                            </>
                          )}
                          {q?.open_pct != null && (
                            <span className={`rec-open ${q.open_pct >= 0 ? 'up' : 'down'}`}>
                              {hist.length > 0 ? ' ' : ''}open {q.open_pct > 0 ? '+' : ''}{q.open_pct}%
                            </span>
                          )}
                        </span>
                      );
                    })()}
                  </td>
                  <td className="date-cell">{entry.first_discovered}</td>
                  <td className="count-cell">
                    <span className={`count-badge ${entry.times_found > 1 ? 'recurring' : 'new'}`}>
                      {entry.times_found}
                    </span>
                  </td>
                  <td className="date-cell">{entry.last_found}</td>
                  <td className="sector-cell">{entry.first_sector}</td>
                  <td className="appearances-cell">
                    {entry.appearances?.join(', ') || entry.first_discovered}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Rolling Watchlist - Most Recent 50 Unique Stocks */}
      {logData?.entries && logData.entries.length > 0 && (
        <div className="watchlist-section">
          <div className="watchlist-box">
            <span className="watchlist-label">Most recent 50 stocks:</span>
            <span className="watchlist-tickers">
              {[...logData.entries]
                .sort((a, b) =>
                  (new Date(b.last_found) - new Date(a.last_found)) ||
                  (new Date(b.first_discovered) - new Date(a.first_discovered)))
                .slice(0, 50)
                .map(entry => entry.symbol)
                .join(',')}
            </span>
          </div>
        </div>
      )}

      <footer className="logbook-footer">
        <p>Pro Stock Money BOWL - Stock Discovery Logbook</p>
      </footer>

      {/* 🏢 Company Description Popup */}
      {companyPopup.show && companyPopup.data && (
        <div className="company-popup">
          <div className="company-popup-content">
            <div className="company-popup-header">
              <span className="company-popup-symbol">{companyPopup.data.symbol}</span>
              <span className="company-popup-title">{companyPopup.data.company_name}</span>
              <button className="company-popup-close" onClick={closeCompanyPopup}>×</button>
            </div>
            <div className="company-popup-details">
              {companyPopup.data.business_description && (
                <div className="company-popup-industry">
                  <span className="industry-label">{companyPopup.data.business_description}</span>
                </div>
              )}
              <div className="company-popup-description">
                {companyPopup.loading ? (
                  <p><em>Loading description…</em></p>
                ) : (
                  <p>{companyPopup.data.company_description_full || "Company description not available."}</p>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Logbook;
